import { useTranslation } from 'react-i18next';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Flex,
  Skeleton,
  Text,
} from '@kognitos/lattice';

import { useRetrieveWorkspaceBook } from '@/generated/hooks/bdk/v2/services/library';
import type {
  V1AutomationConfig,
  V1CallArg,
} from '@/generated/hooks/grimoire/models';
import { toDisplayValue } from '@/modules/automation-inputs/utils/valueConversion';
import type { IResolvedStepConnection } from '@/modules/automation/utils/resolve-step-connections';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { convertToDataUrl } from '@/shared/utils/image-utils';
import {
  getInitials,
  getIntegrationDisplayName,
} from '@/shared/utils/integrations-utils';
import { formatProcedureLabel } from '@/shared/utils/procedure-label';

/** Formats one call argument. Config-backed args resolve to their actual
 *  current value (the same automation config a business user can tune in
 *  the Config tab) rather than the raw `kognitos.config.<name>` reference —
 *  seeing "confirmed" is more useful here than seeing the code path. */
function formatCallArg(
  arg: V1CallArg,
  automationConfig: V1AutomationConfig | undefined,
  tAutomation: (key: string) => string,
): string {
  if (arg.literal !== undefined) {
    return `${arg.name} = ${String(toDisplayValue(arg.literal))}`;
  }
  if (arg.config) {
    const configValue = automationConfig?.[arg.config]?.value;
    return `${arg.name} = ${String(toDisplayValue(configValue))}`;
  }
  return `${arg.name} (${tAutomation('automation.manifestSopView.connections.computed')})`;
}

interface IConnectionGroupProps {
  connection: IResolvedStepConnection;
  automationConfig: V1AutomationConfig | undefined;
}

/** One connection's icon + display name, with the functions this step calls
 *  on it listed underneath. The icon/name fetch (`useRetrieveWorkspaceBook`)
 *  uses the same hook and params as `AutomationIntegrationItem`, so it's
 *  typically a cache hit rather than a fresh network round-trip — the
 *  automation's integrations panel already warms this same query. */
function ConnectionGroup({
  connection,
  automationConfig,
}: IConnectionGroupProps) {
  const { t: tAutomation } = useTranslation('automation');
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const { data, isLoading } = useRetrieveWorkspaceBook(
    organizationId,
    workspaceId,
    connection.book ?? '',
    connection.bookVersion ?? '',
    {
      query: {
        enabled: Boolean(
          organizationId &&
            workspaceId &&
            connection.book &&
            connection.bookVersion,
        ),
      },
    },
  );

  const book = data?.data.book;
  const imageSrc = convertToDataUrl(book?.icon);
  const displayName = book
    ? getIntegrationDisplayName(book)
    : connection.connectionAlias;

  return (
    <Flex direction="column" gap={2}>
      <Flex align="center" gap={2}>
        {isLoading ? (
          <Skeleton className="size-5 shrink-0 rounded" />
        ) : (
          <Avatar className="size-5 shrink-0 rounded">
            {imageSrc && (
              <AvatarImage
                src={imageSrc}
                alt={displayName}
                className="object-contain"
              />
            )}
            <AvatarFallback className="rounded bg-muted text-[10px] font-medium text-muted-foreground">
              {getInitials(displayName)}
            </AvatarFallback>
          </Avatar>
        )}
        <Text level="small" weight="medium">
          {displayName}
        </Text>
      </Flex>
      <Flex direction="column" gap={2} className="pl-7">
        {connection.calls.map(({ functionName, call }, index) => (
          <Flex key={`${functionName}-${index}`} direction="column" gap={1}>
            {/* wrap: else the badge squeezes the label onto two lines. */}
            <Flex align="center" gap={2} wrap="wrap" className="min-w-0">
              <Text level="small" title={functionName} className="break-words">
                {formatProcedureLabel(functionName, tAutomation)}
              </Text>
              {call.is_mutating && (
                <Badge variant="warning">
                  {tAutomation(
                    'automation.manifestSopView.connections.mutating',
                  )}
                </Badge>
              )}
            </Flex>
            {(call.args ?? []).length > 0 && (
              <Flex direction="column" gap={1} className="pl-4">
                {(call.args ?? []).map((arg, argIndex) => (
                  <Text
                    key={`${arg.name}-${argIndex}`}
                    level="xSmall"
                    color="muted"
                  >
                    {formatCallArg(arg, automationConfig, tAutomation)}
                  </Text>
                ))}
              </Flex>
            )}
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
}

export interface IManifestConnectionsTabProps {
  connections: IResolvedStepConnection[];
  /** Resolves `config`-backed call args to their actual current value. */
  automationConfig?: V1AutomationConfig;
}

/** A step's external calls, grouped by connection and shown with each
 *  connection's real icon and name — sourced from `V1Audit.external_calls`,
 *  parsed and resolved via `resolveStepConnections`. */
export function ManifestConnectionsTab({
  connections,
  automationConfig,
}: IManifestConnectionsTabProps) {
  return (
    <Flex direction="column" gap={4}>
      {connections.map(connection => (
        <ConnectionGroup
          key={connection.connectionAlias}
          connection={connection}
          automationConfig={automationConfig}
        />
      ))}
    </Flex>
  );
}
